import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money } from "@/dealership/lib/api";
import { toast } from "sonner";
import PageHeader from "@/dealership/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, X, ArrowDownRight, ArrowUpRight, CheckCircle2 } from "lucide-react";

const STATUSES = ["In Stock", "Reconditioning", "Available", "Pending", "Sold"];
const SOURCES = ["Auction", "Trade-in", "Private", "Wholesale"];
const BODY_STYLES = ["Sedan", "Coupe", "Hatchback", "SUV", "Crossover", "Pickup Truck", "Minivan", "Cargo Van", "Passenger Van", "Wagon", "Convertible", "Sports Car", "Other"];
const FUEL_TYPES = ["Gasoline", "Diesel", "Hybrid", "Plug-in Hybrid", "Electric", "Other"];

const statusStyle = {
  "In Stock": "bg-blue-500/12 text-blue-400",
  Reconditioning: "bg-amber-500/12 text-amber-400",
  Available: "bg-green-500/12 text-green-400",
  Pending: "bg-purple-500/12 text-purple-400",
  Sold: "bg-zinc-500/20 text-zinc-400",
};

const empty = {
  stock_number: "", vin: "", year: "", make: "", model: "", trim: "", color: "",
  body_type: "", fuel_type: "", odometer: "", source: "Auction", purchase_date: "",
  purchase_cost: "", auction_fee: "", transport_fee: "", recon_items: [],
  other_costs: "", tax_included: false, listing_price: "", status: "In Stock", notes: "",
  pricing_description: "",
};

const HST_RATE = 0.13;

const num = (v) => (v === "" || v == null ? 0 : Number(v));

export default function Inventory() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const { data: vehicles = [], isLoading } = useQuery({
    queryKey: ["inventory"],
    queryFn: async () => (await api.get("/inventory")).data,
  });

  const { data: matrix = [] } = useQuery({
    queryKey: ["price-matrix"],
    queryFn: async () => (await api.get("/pricing/matrix")).data,
  });
  const marketByVehicle = {};
  matrix.forEach((m) => { if (m.check) marketByVehicle[m.id] = m.check; });

  useEffect(() => {
    const eid = searchParams.get("edit");
    if (eid && vehicles.length) {
      const v = vehicles.find((x) => x.id === eid);
      if (v) { openEdit(v); setSearchParams({}, { replace: true }); }
    }
  }, [searchParams, vehicles]); // eslint-disable-line

  const saveMut = useMutation({
    mutationFn: async (f) => {
      const body = {
        ...f,
        year: num(f.year), odometer: num(f.odometer),
        purchase_cost: num(f.purchase_cost), auction_fee: num(f.auction_fee),
        transport_fee: num(f.transport_fee), other_costs: num(f.other_costs),
        tax_included: !!f.tax_included,
        listing_price: num(f.listing_price),
        recon_items: f.recon_items.map((r) => ({ description: r.description, cost: num(r.cost) })),
      };
      if (editing) return (await api.put(`/inventory/${editing}`, body)).data;
      return (await api.post("/inventory", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(editing ? "Vehicle updated" : "Vehicle added");
      setOpen(false);
    },
    onError: () => toast.error("Failed to save vehicle"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/inventory/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Vehicle deleted");
    },
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v.id);
    setForm({
      ...empty, ...v,
      recon_items: (v.recon_items || []).map((r) => ({ ...r })),
    });
    setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const liveTax = form.tax_included ? num(form.purchase_cost) * HST_RATE : 0;
  const liveTotal =
    num(form.purchase_cost) + num(form.auction_fee) + num(form.transport_fee) +
    num(form.other_costs) + form.recon_items.reduce((s, r) => s + num(r.cost), 0) +
    liveTax;

  const addRecon = () => set("recon_items", [...form.recon_items, { description: "", cost: "" }]);
  const updRecon = (i, k, v) => {
    const arr = [...form.recon_items];
    arr[i] = { ...arr[i], [k]: v };
    set("recon_items", arr);
  };
  const rmRecon = (i) => set("recon_items", form.recon_items.filter((_, idx) => idx !== i));

  return (
    <div className="p-8">
      <PageHeader
        title="Inventory"
        subtitle="Manage units, true landed cost and front-line readiness."
        action={
          <Button onClick={openNew} data-testid="add-vehicle-button">
            <Plus className="h-4 w-4 mr-2" /> Add Vehicle
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Stock #</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead>VIN</TableHead>
              <TableHead className="text-right">Total Cost</TableHead>
              <TableHead className="text-right">List Price</TableHead>
              <TableHead className="text-right">Potential</TableHead>
              <TableHead>Market</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : vehicles.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No vehicles yet. Add your first unit.</TableCell></TableRow>
            ) : (
              vehicles.map((v) => {
                const potential = num(v.listing_price) - num(v.total_cost);
                const check = marketByVehicle[v.id];
                const rec = check?.recommendation;
                return (
                  <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`} className="cursor-pointer" onClick={() => navigate(`/dealership/inventory/${v.id}`)}>
                    <TableCell className="font-mono-data text-xs">{v.stock_number}</TableCell>
                    <TableCell className="font-semibold">
                      {v.year} {v.make} {v.model}
                      <span className="block text-xs text-muted-foreground font-normal">{v.trim} · {v.color} · {Number(v.odometer).toLocaleString()} km</span>
                    </TableCell>
                    <TableCell className="font-mono-data text-xs">{v.vin}</TableCell>
                    <TableCell className="text-right font-mono-data">{money(v.total_cost)}</TableCell>
                    <TableCell className="text-right font-mono-data">{money(v.listing_price)}</TableCell>
                    <TableCell className={`text-right font-mono-data ${potential >= 0 ? "text-[#16A34A]" : "text-destructive"}`}>{money(potential)}</TableCell>
                    <TableCell>
                      {rec ? (
                        rec.action === "hold" ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-400 bg-green-500/[0.07] rounded-full px-2 py-0.5" data-testid={`market-pill-${v.id}`} title="Priced in line with market comps">
                            <CheckCircle2 className="h-3 w-3" /> At market
                          </span>
                        ) : (
                          <span
                            className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 font-semibold ${rec.action === "lower" ? "text-red-400 bg-red-500/[0.07]" : "text-green-400 bg-green-500/[0.07]"}`}
                            data-testid={`market-pill-${v.id}`}
                            title={rec.reason}
                            onClick={(e) => { e.stopPropagation(); navigate("/dealership/pricing"); }}
                          >
                            {rec.action === "lower" ? <ArrowDownRight className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}
                            {money(rec.suggested_price)}
                          </span>
                        )
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell><Badge className={`${statusStyle[v.status]} border-0`}>{v.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(v); }} data-testid={`edit-vehicle-${v.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); delMut.mutate(v.id); }} data-testid={`delete-vehicle-${v.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-4 py-2">
            <div className="space-y-2"><Label className="label-mono">Stock #</Label><Input value={form.stock_number} onChange={(e) => set("stock_number", e.target.value)} placeholder="auto" data-testid="vehicle-stock-input" /></div>
            <div className="col-span-2 space-y-2"><Label className="label-mono">VIN *</Label><Input value={form.vin} onChange={(e) => set("vin", e.target.value)} className="font-mono-data" data-testid="vehicle-vin-input" /></div>
            <div className="space-y-2"><Label className="label-mono">Year *</Label><Input type="number" value={form.year} onChange={(e) => set("year", e.target.value)} data-testid="vehicle-year-input" /></div>
            <div className="space-y-2"><Label className="label-mono">Make *</Label><Input value={form.make} onChange={(e) => set("make", e.target.value)} data-testid="vehicle-make-input" /></div>
            <div className="space-y-2"><Label className="label-mono">Model *</Label><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="vehicle-model-input" /></div>
            <div className="space-y-2"><Label className="label-mono">Trim</Label><Input value={form.trim} onChange={(e) => set("trim", e.target.value)} /></div>
            <div className="space-y-2"><Label className="label-mono">Color</Label><Input value={form.color} onChange={(e) => set("color", e.target.value)} /></div>
            <div className="space-y-2"><Label className="label-mono">Body Style</Label>
              <Select value={form.body_type || undefined} onValueChange={(v) => set("body_type", v)}>
                <SelectTrigger data-testid="vehicle-body-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{BODY_STYLES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label className="label-mono">Fuel Type</Label>
              <Select value={form.fuel_type || undefined} onValueChange={(v) => set("fuel_type", v)}>
                <SelectTrigger data-testid="vehicle-fuel-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{FUEL_TYPES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label className="label-mono">Odometer (km)</Label><Input type="number" value={form.odometer} onChange={(e) => set("odometer", e.target.value)} data-testid="vehicle-odometer-input" /></div>
            <div className="space-y-2"><Label className="label-mono">Source</Label>
              <Select value={form.source} onValueChange={(v) => set("source", v)}>
                <SelectTrigger data-testid="vehicle-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label className="label-mono">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="vehicle-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="col-span-3 border-t border-border pt-3 mt-1">
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Cost Tracking</p>
            </div>
            <div className="space-y-2"><Label className="label-mono">Purchase Cost</Label><Input type="number" value={form.purchase_cost} onChange={(e) => set("purchase_cost", e.target.value)} data-testid="vehicle-purchase-input" /></div>
            <div className="space-y-2"><Label className="label-mono">Auction Fee</Label><Input type="number" value={form.auction_fee} onChange={(e) => set("auction_fee", e.target.value)} data-testid="vehicle-auction-input" /></div>
            <div className="space-y-2"><Label className="label-mono">Transport Fee</Label><Input type="number" value={form.transport_fee} onChange={(e) => set("transport_fee", e.target.value)} /></div>

            <div className="col-span-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="label-mono">Reconditioning / Repairs</Label>
                <Button type="button" variant="outline" size="sm" onClick={addRecon} data-testid="add-recon-button"><Plus className="h-3 w-3 mr-1" />Add item</Button>
              </div>
              {form.recon_items.map((r, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input placeholder="Description" value={r.description} onChange={(e) => updRecon(i, "description", e.target.value)} className="flex-1" />
                  <Input type="number" placeholder="Cost" value={r.cost} onChange={(e) => updRecon(i, "cost", e.target.value)} className="w-32" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => rmRecon(i)}><X className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>

            <div className="space-y-2"><Label className="label-mono">Other Costs</Label><Input type="number" value={form.other_costs} onChange={(e) => set("other_costs", e.target.value)} /></div>
            <div className="space-y-2">
              <Label className="label-mono">Purchase Tax (HST 13%)</Label>
              <div className="h-10 flex items-center justify-between px-3 rounded-md border border-border">
                <div className="flex items-center gap-2">
                  <Switch checked={!!form.tax_included} onCheckedChange={(v) => set("tax_included", v)} data-testid="vehicle-tax-toggle" />
                  <span className="text-sm">{form.tax_included ? "On" : "Off"}</span>
                </div>
                <span className="font-mono-data text-sm" data-testid="vehicle-tax-amount">{money(liveTax)}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">Auto-calculated at 13% on Purchase Cost. Included in landed cost & profit metrics.</p>
            </div>
            <div className="space-y-2"><Label className="label-mono">Listing Price</Label><Input type="number" value={form.listing_price} onChange={(e) => set("listing_price", e.target.value)} data-testid="vehicle-listing-input" /></div>
            <div className="space-y-2">
              <Label className="label-mono">Total Landed Cost</Label>
              <div className="h-10 flex items-center px-3 rounded-md bg-secondary font-mono-data font-semibold" data-testid="vehicle-total-cost">{money(liveTotal)}</div>
              <p className="text-[11px] text-muted-foreground">{form.tax_included ? "Includes 13% purchase HST." : "Tax not applied."}</p>
            </div>
            <div className="col-span-3 space-y-2"><Label className="label-mono">Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
            <div className="col-span-3 space-y-2">
              <Label className="label-mono">Car Details for Price AI</Label>
              <Textarea value={form.pricing_description} onChange={(e) => set("pricing_description", e.target.value)} placeholder="Everything this specific car comes with: new tires, remaining warranty, extra options, clean Carfax…" data-testid="vehicle-pricing-description" />
              <p className="text-[11px] text-muted-foreground">Used by the Price Matrix market scan for this car only, on top of the global "About our cars" facts.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate(form)} disabled={!form.vin || !form.make || !form.model || saveMut.isPending} data-testid="save-vehicle-button">
              {editing ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
